import { useEffect, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Spinner } from '@/components/ui'
import { setPersonaAssetGrants } from '@/services/assets'
import {
  usePersonaAssetGrants,
  personaAssetGrantsQueryKey,
} from '@/hooks/usePersonaAssetGrants'

export function PersonaSendableAssetsSection({
  personaId,
  hideHeader = false,
}: {
  personaId: string
  hideHeader?: boolean
}) {
  const { activeAssets, grantAll: serverGrantAll, grantedIds, isLoading, dataUpdatedAt } =
    usePersonaAssetGrants(personaId)
  const queryClient = useQueryClient()
  const [grantAll, setGrantAll] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)

  // Local editing state seeds from the shared cache on load/persona change,
  // and again whenever the grants query's data actually changes — including
  // a background refetch triggered by a sibling mount's write (e.g. the
  // legacy editor modal coexisting over the workshop card, SA-47). Keyed on
  // `dataUpdatedAt` rather than `grantedIds`/`serverGrantAll` directly:
  // `grantedIds` is a new array reference every render, which would loop.
  // Guarded on `saving` so an in-flight save by THIS section doesn't get
  // clobbered by its own invalidation-triggered refetch.
  useEffect(() => {
    if (isLoading || saving) return
    setGrantAll(serverGrantAll)
    setSelected(new Set(grantedIds))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed on load/persona change/refetch only; `saving` is read, not a dep, by design
  }, [personaId, isLoading, dataUpdatedAt])

  const persist = useCallback(
    async (nextGrantAll: boolean, nextIds: Set<string>) => {
      setSaving(true)
      try {
        await setPersonaAssetGrants(personaId, {
          grantAll: nextGrantAll,
          assetIds: Array.from(nextIds),
        })
        // Keep the workshop status row / other mounts in sync (SA-47).
        queryClient.invalidateQueries({
          queryKey: personaAssetGrantsQueryKey(personaId),
        })
      } finally {
        setSaving(false)
      }
    },
    [personaId, queryClient],
  )

  const toggleAll = async () => {
    const next = !grantAll
    setGrantAll(next)
    await persist(next, selected)
  }

  const toggleAsset = async (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelected(next)
    await persist(grantAll, next)
  }

  if (isLoading) return <Spinner size="sm" />

  return (
    <div className="space-y-3">
      {!hideHeader && (
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Assets this persona can send
          </h3>
          <p className="text-xs text-muted-foreground">
            Controls which approved assets the AI may send to customers.
          </p>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={grantAll} onChange={toggleAll} disabled={saving} />
        <span>Can send any of my approved assets</span>
      </label>
      {!grantAll && (
        <ul className="space-y-1">
          {activeAssets.map((a) => (
            <li key={a.id}>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.has(a.id)}
                  onChange={() => toggleAsset(a.id)}
                  disabled={saving}
                />
                <span>{a.name}</span>
                <span className="text-xs text-muted-foreground">({a.slug})</span>
              </label>
            </li>
          ))}
          {activeAssets.length === 0 && (
            <li className="text-xs text-muted-foreground">
              No approved assets yet — add some on the Assets page.
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
